import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Container, Badge, Button, Form, Spinner, Toast, ToastContainer } from 'react-bootstrap';
import { useNavigate, Navigate } from 'react-router-dom';
import { GET_MY_TASKS, UPDATE_TASK } from '../graphql/operations';

// ─── Constants ────────────────────────────────────────────────────────────────

const COLUMNS = [
  {
    key: 'todo',
    label: 'To Do',
    icon: 'fas fa-circle',
    color: '#64748b',
    bg: '#f1f5f9',
    border: '#cbd5e1',
  },
  {
    key: 'in_progress',
    label: 'In Progress',
    icon: 'fas fa-spinner',
    color: '#6366f1',
    bg: '#eef2ff',
    border: '#c7d2fe',
  },
  {
    key: 'done',
    label: 'Done',
    icon: 'fas fa-check-circle',
    color: '#10b981',
    bg: '#ecfdf5',
    border: '#a7f3d0',
  },
  {
    key: 'cancelled',
    label: 'Cancelled',
    icon: 'fas fa-times-circle',
    color: '#ef4444',
    bg: '#fef2f2',
    border: '#fecaca',
  },
];

const PRIORITY_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  low: { label: 'Low', color: '#10b981', bg: '#ecfdf5' },
  medium: { label: 'Medium', color: '#f59e0b', bg: '#fffbeb' },
  high: { label: 'High', color: '#f97316', bg: '#fff7ed' },
  urgent: { label: 'Urgent', color: '#ef4444', bg: '#fef2f2' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateStr: string) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const isDueSoon = (dateStr: string) => {
  if (!dateStr) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr);
  const diffDays = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  return diffDays >= 0 && diffDays <= 3;
};

const isOverdue = (dateStr: string, status: string) => {
  if (!dateStr || status === 'done' || status === 'cancelled') return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(dateStr) < today;
};

// ─── TaskCard Component ───────────────────────────────────────────────────────

interface TaskCardProps {
  task: any;
  onStatusChange: (task: any, status: string) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, onStatusChange }) => {
  const priority = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
  const overdue = isOverdue(task.dueDate, task.status);
  const dueSoon = isDueSoon(task.dueDate);
  const assignedBy = task.assignedByUser?.username || 'Admin';

  const nextStatus: Record<string, string> = {
    todo: 'in_progress',
    in_progress: 'done',
    done: 'todo',
    cancelled: 'todo',
  };

  const prevStatus: Record<string, string> = {
    in_progress: 'todo',
    done: 'in_progress',
    cancelled: 'in_progress',
  };

  return (
    <div
      style={{
        background: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        padding: '1rem',
        marginBottom: '0.75rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        transition: 'box-shadow 0.2s, transform 0.2s',
        position: 'relative',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)';
        (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)';
        (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
      }}
    >
      {/* Priority accent bar */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '4px',
          height: '100%',
          background: priority.color,
          borderRadius: '12px 0 0 12px',
        }}
      />

      <div style={{ paddingLeft: '4px' }}>
        {/* Header row */}
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span
              style={{
                fontSize: '0.7rem',
                fontWeight: 700,
                color: '#94a3b8',
                fontFamily: 'monospace',
                letterSpacing: '0.03em',
              }}
            >
              {task.taskId}
            </span>
            <span
              style={{
                fontSize: '0.7rem',
                fontWeight: 600,
                color: priority.color,
                background: priority.bg,
                borderRadius: '20px',
                padding: '1px 8px',
                border: `1px solid ${priority.color}30`,
              }}
            >
              {priority.label}
            </span>
            {task.category && (
              <span
                style={{
                  fontSize: '0.7rem',
                  color: '#6366f1',
                  background: '#eef2ff',
                  borderRadius: '20px',
                  padding: '1px 8px',
                  fontWeight: 600,
                }}
              >
                {task.category}
              </span>
            )}
          </div>
        </div>

        {/* Title */}
        <div className="fw-semibold mb-1" style={{ fontSize: '0.92rem', color: '#1e293b', lineHeight: 1.35 }}>
          {task.title}
        </div>

        {/* Description */}
        {task.description && (
          <div
            style={{
              fontSize: '0.82rem',
              color: '#64748b',
              lineHeight: 1.4,
              marginBottom: '0.6rem',
            }}
          >
            {task.description}
          </div>
        )}

        {/* Notes if present */}
        {task.notes && (
          <div
            className="p-2 mb-2 rounded"
            style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', fontSize: '0.75rem', color: '#475569' }}
          >
            <i className="fas fa-sticky-note me-1 text-warning" />
            <span className="fw-semibold">Note: </span>{task.notes}
          </div>
        )}

        {/* Footer row */}
        <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
          {/* Assigned By */}
          <div className="d-flex align-items-center gap-1" style={{ fontSize: '0.75rem', color: '#64748b' }}>
            <i className="fas fa-user-shield text-indigo me-1" style={{ color: '#6366f1' }} />
            <span>Assigned by: <strong>{assignedBy}</strong></span>
          </div>

          <div className="d-flex align-items-center gap-2">
            {/* Due date */}
            {task.dueDate && (
              <span
                style={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  color: overdue ? '#ef4444' : dueSoon ? '#f97316' : '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <i className={`fas fa-${overdue ? 'exclamation-triangle' : 'calendar-alt'}`} />
                {formatDate(task.dueDate)}
              </span>
            )}

            {/* Back button — move to previous status */}
            {prevStatus[task.status] && (
              <button
                onClick={() => onStatusChange(task, prevStatus[task.status])}
                style={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  padding: '3px 10px',
                  borderRadius: '20px',
                  border: '1.5px solid #94a3b8',
                  background: 'transparent',
                  color: '#64748b',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#64748b'; (e.currentTarget as HTMLElement).style.color = '#fff'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = '#64748b'; }}
                title={`Move back to ${prevStatus[task.status].replace('_', ' ')}`}
              >
                <i className="fas fa-arrow-left me-1" />
                {/* Back */}
              </button>
            )}

            {/* Quick status advance button */}
            {task.status !== 'done' && task.status !== 'cancelled' && (
              <button
                onClick={() => onStatusChange(task, nextStatus[task.status])}
                style={{
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  padding: '3px 10px',
                  borderRadius: '20px',
                  border: '1.5px solid #10b981',
                  background: 'transparent',
                  color: '#10b981',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#10b981'; (e.currentTarget as HTMLElement).style.color = '#fff'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = '#10b981'; }}
                title={`Mark as ${nextStatus[task.status].replace('_', ' ')}`}
              >
                <i className="fas fa-arrow-right me-1" />
                {/* {nextStatus[task.status] === 'in_progress' ? 'Start Task' : 'Complete'} */}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main MyTasksPage Component ─────────────────────────────────────────────

const MyTasksPage: React.FC = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role') || 'customer';
  const username = localStorage.getItem('username') || 'Employee';

  // State
  const [viewMode, setViewMode] = useState<'kanban' | 'table'>('kanban');
  const [searchText, setSearchText] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [toastInfo, setToastInfo] = useState<{ show: boolean; msg: string; bg: string }>({
    show: false,
    msg: '',
    bg: 'success',
  });

  // Guard: Customers or unauthenticated users must not view this page
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  if (role === 'customer') {
    return <Navigate to="/my-orders" replace />;
  }

  // GraphQL Query & Mutation
  const { data, loading, error, refetch } = useQuery(GET_MY_TASKS, {
    fetchPolicy: 'network-only',
  });

  const [updateTaskMutation] = useMutation(UPDATE_TASK, {
    onCompleted: () => refetch(),
  });

  const myTasks: any[] = data?.myTasks || [];

  // Filtering
  const filteredTasks = useMemo(() => {
    return myTasks.filter(t => {
      if (searchText) {
        const q = searchText.toLowerCase();
        if (
          !t.title.toLowerCase().includes(q) &&
          !(t.description || '').toLowerCase().includes(q) &&
          !(t.taskId || '').toLowerCase().includes(q)
        ) return false;
      }
      if (filterPriority !== 'all' && t.priority !== filterPriority) return false;
      if (filterStatus !== 'all' && t.status !== filterStatus) return false;
      return true;
    });
  }, [myTasks, searchText, filterPriority, filterStatus]);

  // Statistics
  const stats = useMemo(() => {
    const total = myTasks.length;
    const todo = myTasks.filter(t => t.status === 'todo').length;
    const inProgress = myTasks.filter(t => t.status === 'in_progress').length;
    const done = myTasks.filter(t => t.status === 'done').length;
    const overdue = myTasks.filter(t => isOverdue(t.dueDate, t.status)).length;
    return { total, todo, inProgress, done, overdue };
  }, [myTasks]);

  // Handler for quick status change
  const handleStatusChange = async (task: any, newStatus: string) => {
    try {
      const result = await updateTaskMutation({
        variables: { id: task.id, status: newStatus },
      });
      const res = result.data?.updateTask;
      if (res?.success) {
        const statusLabel = newStatus.replace('_', ' ');
        setToastInfo({
          show: true,
          msg: `Task "${task.title}" status updated to ${statusLabel.toUpperCase()}`,
          bg: 'success',
        });
      }
    } catch (err: any) {
      setToastInfo({
        show: true,
        msg: err.message || 'Failed to update task status.',
        bg: 'danger',
      });
    }
  };

  const tasksByColumn = (colKey: string) =>
    filteredTasks.filter(t => t.status === colKey);

  return (
    <div style={{ background: '#f8fafc', minHeight: 'calc(100vh - 120px)', padding: '2rem 0' }}>
      <Container fluid style={{ maxWidth: '1400px' }}>

        {/* Header section */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <div className="d-flex align-items-center gap-2">
              <h2
                className="fw-bold mb-0"
                style={{
                  background: 'linear-gradient(135deg, #e91e8c 0%, #3b5bdb 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                <i className="fas fa-tasks me-2" style={{ WebkitTextFillColor: '#e91e8c' }} />
                My Tasks
              </h2>
              <Badge bg="secondary" style={{ textTransform: 'capitalize', fontSize: '0.75rem' }}>
                {role}
              </Badge>
            </div>
            <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.9rem' }}>
              Welcome back, <strong>{username}</strong>! Here are all the tasks assigned to you by management.
            </p>
          </div>

          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              onClick={() => setViewMode(v => v === 'kanban' ? 'table' : 'kanban')}
              style={{ borderRadius: '10px', fontSize: '0.85rem', padding: '0.5rem 1rem' }}
            >
              <i className={`fas ${viewMode === 'kanban' ? 'fa-table' : 'fa-columns'} me-2`} />
              {viewMode === 'kanban' ? 'Table View' : 'Kanban View'}
            </Button>
            <Button
              variant="outline-primary"
              onClick={() => refetch()}
              style={{ borderRadius: '10px', fontSize: '0.85rem', padding: '0.5rem 1rem' }}
              title="Refresh tasks"
            >
              <i className="fas fa-sync-alt me-1" /> Refresh
            </Button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="d-flex gap-3 mb-4 flex-wrap">
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '1rem 1.25rem',
              border: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              flex: 1,
              minWidth: 140,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}
          >
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: '10px',
                background: '#6366f118',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <i className="fas fa-clipboard-list" style={{ color: '#6366f1', fontSize: '1.1rem' }} />
            </div>
            <div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.1 }}>{stats.total}</div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>Assigned Tasks</div>
            </div>
          </div>

          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '1rem 1.25rem',
              border: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              flex: 1,
              minWidth: 140,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}
          >
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: '10px',
                background: '#64748b18',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <i className="fas fa-circle" style={{ color: '#64748b', fontSize: '1.1rem' }} />
            </div>
            <div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.1 }}>{stats.todo}</div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>To Do</div>
            </div>
          </div>

          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '1rem 1.25rem',
              border: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              flex: 1,
              minWidth: 140,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}
          >
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: '10px',
                background: '#6366f118',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <i className="fas fa-spinner" style={{ color: '#6366f1', fontSize: '1.1rem' }} />
            </div>
            <div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.1 }}>{stats.inProgress}</div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>In Progress</div>
            </div>
          </div>

          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '1rem 1.25rem',
              border: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              flex: 1,
              minWidth: 140,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}
          >
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: '10px',
                background: '#10b98118',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <i className="fas fa-check-circle" style={{ color: '#10b981', fontSize: '1.1rem' }} />
            </div>
            <div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.1 }}>{stats.done}</div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>Completed</div>
            </div>
          </div>

          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '1rem 1.25rem',
              border: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              flex: 1,
              minWidth: 140,
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            }}
          >
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: '10px',
                background: '#ef444418',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <i className="fas fa-exclamation-triangle" style={{ color: '#ef4444', fontSize: '1.1rem' }} />
            </div>
            <div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1e293b', lineHeight: 1.1 }}>{stats.overdue}</div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>Overdue</div>
            </div>
          </div>
        </div>

        {/* Filter bar */}
        <div
          style={{
            background: '#fff',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            padding: '0.875rem 1rem',
            marginBottom: '1.25rem',
            display: 'flex',
            gap: '0.75rem',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <div style={{ position: 'relative', flex: '1', minWidth: 200 }}>
            <i
              className="fas fa-search"
              style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.8rem' }}
            />
            <Form.Control
              type="text"
              placeholder="Search your tasks..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              style={{
                paddingLeft: '2.1rem',
                borderRadius: '8px',
                border: '1.5px solid #e2e8f0',
                fontSize: '0.85rem',
                height: '38px',
              }}
            />
          </div>

          <Form.Select
            value={filterPriority}
            onChange={e => setFilterPriority(e.target.value)}
            style={{ maxWidth: 160, borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: '0.85rem', height: '38px' }}
          >
            <option value="all">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </Form.Select>

          <Form.Select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            style={{ maxWidth: 160, borderRadius: '8px', border: '1.5px solid #e2e8f0', fontSize: '0.85rem', height: '38px' }}
          >
            <option value="all">All Statuses</option>
            <option value="todo">To Do</option>
            <option value="in_progress">In Progress</option>
            <option value="done">Done</option>
            <option value="cancelled">Cancelled</option>
          </Form.Select>

          {(searchText || filterPriority !== 'all' || filterStatus !== 'all') && (
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => { setSearchText(''); setFilterPriority('all'); setFilterStatus('all'); }}
              style={{ borderRadius: '8px', fontSize: '0.8rem', height: '38px' }}
            >
              <i className="fas fa-times me-1" />Clear
            </Button>
          )}
        </div>

        {/* Loading state */}
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: '#e91e8c' }} />
            <div className="mt-2 text-muted" style={{ fontSize: '0.9rem' }}>Loading your tasks...</div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="alert alert-danger rounded-3 my-3">
            <i className="fas fa-exclamation-circle me-2" />
            Failed to load tasks: {error.message}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && myTasks.length === 0 && (
          <div className="bg-white rounded-3 p-5 text-center shadow-sm my-4 border">
            <div
              className="mx-auto rounded-circle d-flex align-items-center justify-content-center mb-3"
              style={{ width: 64, height: 64, background: '#f1f5f9', color: '#94a3b8' }}
            >
              <i className="fas fa-tasks" style={{ fontSize: '24px' }} />
            </div>
            <h5 className="fw-bold text-dark mb-1">No tasks assigned to you</h5>
            <p className="text-muted small">You're all caught up! Your manager will assign tasks here when available.</p>
          </div>
        )}

        {/* ── Kanban View ── */}
        {!loading && !error && myTasks.length > 0 && viewMode === 'kanban' && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1rem',
              alignItems: 'start',
            }}
          >
            {COLUMNS.map(col => {
              const cards = tasksByColumn(col.key);
              return (
                <div
                  key={col.key}
                  style={{
                    background: col.bg,
                    borderRadius: '14px',
                    border: `2px solid ${col.border}`,
                    padding: '1rem',
                    minHeight: 220,
                  }}
                >
                  {/* Column header */}
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <i className={col.icon} style={{ color: col.color, fontSize: '0.9rem' }} />
                      <span style={{ fontWeight: 700, fontSize: '0.9rem', color: col.color }}>
                        {col.label}
                      </span>
                    </div>
                    <span
                      style={{
                        background: col.color,
                        color: '#fff',
                        borderRadius: '20px',
                        padding: '1px 10px',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                      }}
                    >
                      {cards.length}
                    </span>
                  </div>

                  {/* Cards */}
                  {cards.length === 0 ? (
                    <div
                      style={{
                        textAlign: 'center',
                        color: '#94a3b8',
                        fontSize: '0.82rem',
                        padding: '2rem 0',
                        border: `2px dashed ${col.border}`,
                        borderRadius: '8px',
                      }}
                    >
                      <i className="fas fa-inbox mb-2" style={{ fontSize: '1.3rem', display: 'block' }} />
                      No tasks in {col.label}
                    </div>
                  ) : (
                    cards.map(task => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        onStatusChange={handleStatusChange}
                      />
                    ))
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Table View ── */}
        {!loading && !error && myTasks.length > 0 && viewMode === 'table' && (
          <div style={{ background: '#fff', borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            {filteredTasks.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-search mb-2" style={{ fontSize: '1.5rem', display: 'block' }} />
                No matching tasks found.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'linear-gradient(135deg, #e91e8c 0%, #3b5bdb 100%)' }}>
                      {['Task ID', 'Title', 'Category', 'Priority', 'Status', 'Assigned By', 'Due Date', 'Actions'].map(h => (
                        <th
                          key={h}
                          style={{
                            padding: '0.85rem 1rem',
                            color: '#fff',
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            textAlign: 'left',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTasks.map((t, idx) => {
                      const priority = PRIORITY_CONFIG[t.priority] || PRIORITY_CONFIG.medium;
                      const overdue = isOverdue(t.dueDate, t.status);
                      const dueSoon = isDueSoon(t.dueDate);
                      const col = COLUMNS.find(c => c.key === t.status) || COLUMNS[0];
                      return (
                        <tr
                          key={t.id}
                          style={{
                            borderBottom: '1px solid #f1f5f9',
                            background: idx % 2 === 0 ? '#ffffff' : '#fafafa',
                          }}
                        >
                          <td style={{ padding: '0.85rem 1rem', fontFamily: 'monospace', fontWeight: 700, fontSize: '0.78rem', color: '#64748b' }}>
                            {t.taskId}
                          </td>
                          <td style={{ padding: '0.85rem 1rem', fontWeight: 600, fontSize: '0.88rem', color: '#1e293b', maxWidth: 300 }}>
                            {t.title}
                            {t.description && (
                              <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 400 }} className="text-truncate">
                                {t.description}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '0.85rem 1rem', fontSize: '0.8rem' }}>
                            {t.category ? (
                              <span style={{ background: '#eef2ff', color: '#6366f1', padding: '2px 8px', borderRadius: '12px', fontWeight: 600, fontSize: '0.72rem' }}>
                                {t.category}
                              </span>
                            ) : '-'}
                          </td>
                          <td style={{ padding: '0.85rem 1rem' }}>
                            <span style={{ background: priority.bg, color: priority.color, padding: '2px 8px', borderRadius: '12px', fontWeight: 600, fontSize: '0.72rem', border: `1px solid ${priority.color}30` }}>
                              {priority.label}
                            </span>
                          </td>
                          <td style={{ padding: '0.85rem 1rem' }}>
                            <span style={{ background: col.bg, color: col.color, padding: '2px 8px', borderRadius: '12px', fontWeight: 600, fontSize: '0.72rem', border: `1px solid ${col.border}` }}>
                              {col.label}
                            </span>
                          </td>
                          <td style={{ padding: '0.85rem 1rem', fontSize: '0.8rem', color: '#475569', fontWeight: 500 }}>
                            {t.assignedByUser?.username || 'Admin'}
                          </td>
                          <td style={{ padding: '0.85rem 1rem', fontSize: '0.78rem', fontWeight: 600, color: overdue ? '#ef4444' : dueSoon ? '#f97316' : '#64748b' }}>
                            {t.dueDate ? formatDate(t.dueDate) : '-'}
                          </td>
                          <td style={{ padding: '0.85rem 1rem' }}>
                            <div className="d-flex gap-1">
                              {t.status === 'todo' && (
                                <button
                                  className="btn btn-sm btn-outline-primary py-0 px-2"
                                  style={{ fontSize: '0.72rem', borderRadius: '12px' }}
                                  onClick={() => handleStatusChange(t, 'in_progress')}
                                >
                                  Start Task
                                </button>
                              )}
                              {t.status === 'in_progress' && (
                                <>
                                  <button
                                    className="btn btn-sm btn-outline-secondary py-0 px-2"
                                    style={{ fontSize: '0.72rem', borderRadius: '12px' }}
                                    onClick={() => handleStatusChange(t, 'todo')}
                                  >
                                    Back
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-success py-0 px-2"
                                    style={{ fontSize: '0.72rem', borderRadius: '12px' }}
                                    onClick={() => handleStatusChange(t, 'done')}
                                  >
                                    Complete
                                  </button>
                                </>
                              )}
                              {t.status === 'done' && (
                                <button
                                  className="btn btn-sm btn-outline-secondary py-0 px-2"
                                  style={{ fontSize: '0.72rem', borderRadius: '12px' }}
                                  onClick={() => handleStatusChange(t, 'in_progress')}
                                >
                                  Reopen
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </Container>

      {/* Toast Notification Container */}
      <ToastContainer position="bottom-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast
          onClose={() => setToastInfo(prev => ({ ...prev, show: false }))}
          show={toastInfo.show}
          delay={4000}
          autohide
          bg={toastInfo.bg}
          className="text-white"
        >
          <Toast.Body className="d-flex align-items-center justify-content-between">
            <span>{toastInfo.msg}</span>
            <button
              type="button"
              className="btn-close btn-close-white ms-2"
              onClick={() => setToastInfo(prev => ({ ...prev, show: false }))}
            />
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default MyTasksPage;
